/* eslint-disable @typescript-eslint/no-explicit-any */
import {Injectable} from '@angular/core';
import {NgEventBus} from 'ng-event-bus';
import {SpatialUtilService} from '../../../../services/spatial-util.service';
import {AngleUtilService} from '../../../../services/features/angle/angle-util.service';
import {first, of, tap} from 'rxjs';
import {MapToolsService} from '../../../../services/map-tools.service';
import {ConstructionToolModelService} from './construction-tool-model.service';
import {EsriModuleService} from '../../../../esri-nlr/services/esri-module.service';
import {FeatureApplyUpdateService} from '../../../../services/feature-apply-update.service';
import {AngleCreationService} from '../../../../services/features/angle/angle-creation.service';
import {ToolServiceInterface} from './tool-service-interface';

@Injectable({
    providedIn: 'root'
})
export class AngleConstructionToolService implements ToolServiceInterface {
    title = 'AngleConstructionToolService';

    private CREATION_METHOD = {
        PROPERTY: 'creationmethod',
        VALUES: {
            AROUND_LINES: 'YES',
            AROUND_POINTS: 'NO'
        }
    };

    private ANGLE_SIDE = {
        PROPERTY: 'angleside',
        VALUES: {
            INTERNAL: 'YES',
            EXTERNAL: 'NO'
        }
    };

    private HIDE_180_DEGREES = {PROPERTY: 'hide180degrees'};
    private HIDE_ON_MAIN_PLAN = {PROPERTY: 'hideonmainplan'};
    private HIDE_ON_ENLARGEMENTS = {PROPERTY: 'hideonenlargements'};
    private CREATE_INTEREST_ANGLES = {PROPERTY: 'createinterestangles'};

    private allAngles = [];

    attributes: any;

    constructor(
        private angleCreationService: AngleCreationService,
        private angleUtilService: AngleUtilService,
        private constructionToolModelService: ConstructionToolModelService,
        private esriModuleService: EsriModuleService,
        private eventBus: NgEventBus,
        private featuresApplyUpdateService: FeatureApplyUpdateService,
        private mapToolsService: MapToolsService,
        private spatialUtilService: SpatialUtilService
    ) {}

    create(attributes: any) {
        console.debug(`${this.title} - create`);
        return this.mapToolsService.activate('CONSTRUCTION_TOOLS_ANGLE', () => {
            this.allAngles = this.spatialUtilService.getAngles().filter((angle: any) => !angle.attributes.enlargementid);
            this.attributes = structuredClone(attributes);

            const selectedPoints = [...this.spatialUtilService.getHighlightedPoints(), ...this.spatialUtilService.getHighlightedControlPoints()];
            const selectedLines = this.spatialUtilService.getHighlightedLines();

            const valid = this.validate(selectedPoints, selectedLines);
            if (valid) {
                this.attributes = this.angleUtilService.getDefaultAngleAttributes(this.attributes);

                const subscription = this.createAngles(selectedPoints, selectedLines).pipe(first()).subscribe();
                this.eventBus.cast('nlr:busy', subscription);
            }
        });
    }

    private validate(selectedPoints: any[], selectedLines: any[]) {
        console.debug(`${this.title} - validate`);

        const tool = this.getTool();

        tool.errors.length = 0;
        tool.warnings.length = 0;
        tool.information.length = 0;

        if (this.attributes[this.CREATION_METHOD.PROPERTY] === this.CREATION_METHOD.VALUES.AROUND_LINES) {
            if (selectedLines.length < 2) {
                tool.errors.push(tool.errorMessage2);
            } else if (selectedLines.length > 2) {
                tool.errors.push(tool.errorMessage3);
            } else {
                const pointsAssociatedToLines = this.angleUtilService.getRelatedPoints(selectedLines);

                if (pointsAssociatedToLines.length === 0) {
                    tool.errors.push(tool.errorMessage4);
                } else {
                    const angleType = this.angleUtilService.getAngleTypeByLines(selectedLines[0], selectedLines[1]);

                    if (!angleType) {
                        tool.errors.push(tool.errorMessage6);
                    } else {
                        return true;
                    }
                }
            }
        } else if (this.attributes[this.CREATION_METHOD.PROPERTY] === this.CREATION_METHOD.VALUES.AROUND_POINTS) {
            if (selectedPoints.length === 0) {
                tool.errors.push(tool.errorMessage1);
            } else {
                const pointsWithMissingLines = [];

                selectedPoints.forEach((selectedPoint: any) => {
                    const linesAssociatedToPoint = this.angleUtilService.getRelatedLines([selectedPoint], this.spatialUtilService.getLines());

                    if (linesAssociatedToPoint.length < 2) {
                        pointsWithMissingLines.push(selectedPoint);
                    }
                });

                if (pointsWithMissingLines.length > 0) {
                    tool.errors.push(tool.errorMessage5);
                } else {
                    return true;
                }
            }
        }

        return false;
    }

    private getTool() {
        return this.constructionToolModelService.getTool('angle');
    }

    private createAngles(selectedPoints: any[], selectedLines: any[]) {
        console.debug(`${this.title} - createAngles`);

        if (this.attributes[this.CREATION_METHOD.PROPERTY] === this.CREATION_METHOD.VALUES.AROUND_LINES) {
            return this.createAngleByLines(selectedLines);
        } else if (this.attributes[this.CREATION_METHOD.PROPERTY] === this.CREATION_METHOD.VALUES.AROUND_POINTS) {
            return this.createAnglesByPoints(selectedPoints);
        }

        return of(null);
    }

    private setVisibilityAttributes(angles: any[]) {
        console.debug(`${this.title} - setVisibilityAttributes`);

        const enlargements = this.spatialUtilService.getEnlargements();

        angles.forEach(angle => {
            let mainPlanVisibilityValue = this.attributes[this.HIDE_ON_MAIN_PLAN.PROPERTY] ? 'NO' : 'YES';
            let hiddenOnEnlargementIds: any;

            if (
                this.attributes[this.CREATION_METHOD.PROPERTY] === this.CREATION_METHOD.VALUES.AROUND_POINTS &&
                this.attributes[this.HIDE_180_DEGREES.PROPERTY] &&
                angle.attributes.internalangle === '180:00:00'
            ) {
                mainPlanVisibilityValue = 'NO';
            }

            if (
                this.attributes[this.HIDE_ON_ENLARGEMENTS.PROPERTY] ||
                (this.attributes[this.CREATION_METHOD.PROPERTY] === this.CREATION_METHOD.VALUES.AROUND_POINTS &&
                    this.attributes[this.HIDE_180_DEGREES.PROPERTY] &&
                    angle.attributes.internalangle === '180:00:00')
            ) {
                hiddenOnEnlargementIds = enlargements
                    .filter((e: any) => e.geometry.contains(angle.geometry))
                    .map((e: any) => e.attributes.id)
                    .sort()
                    .map((id: any) => `[${id}]`)
                    .join();
            }

            angle.attributes.visible = mainPlanVisibilityValue;
            angle.attributes.hiddenonenlargements = hiddenOnEnlargementIds || null;
        });
    }

    private createAnglesByPoints(selectedPoints: any[]) {
        console.debug(`${this.title} - createAnglesByPoints`);

        const generatedAngles = this.angleCreationService.createMissingCadastralAngles(
            this.allAngles,
            selectedPoints,
            this.spatialUtilService.getLines(),
            this.angleCreationService.ANGLE_GENERATION_METHOD.CONSTRUCTION_TOOLS,
            undefined
        );

        generatedAngles.forEach((angle: any) => {
            angle.attributes.anglederivation = this.attributes.anglederivation.id;
            angle.attributes.angleaccuracy = this.attributes.angleaccuracy.id;
            angle.attributes.measurementsource = this.attributes.measurementsource.id;
        });

        if (this.attributes[this.CREATE_INTEREST_ANGLES.PROPERTY] === true) {
            const generatedInterestAngles = this.angleCreationService.createMissingInterestAngles(
                this.allAngles,
                selectedPoints,
                this.spatialUtilService.getLines(),
                this.angleCreationService.ANGLE_GENERATION_METHOD.CONSTRUCTION_TOOLS
            );

            generatedInterestAngles.forEach((angle: any) => {
                angle.attributes.anglederivation = this.attributes.anglederivation.id;
                angle.attributes.angleaccuracy = this.attributes.angleaccuracy.id;
                angle.attributes.measurementsource = this.attributes.measurementsource.id;
            });

            generatedAngles.push(...generatedInterestAngles);
        }

        this.setVisibilityAttributes(generatedAngles);

        if (generatedAngles.length > 0) {
            return this.angleCreationService.insertAngles(generatedAngles);
        }

        return of(null);
    }

    private createAngleByLines(lines: any[]) {
        console.debug(`${this.title} - createAngleByLines`);

        const point = this.angleUtilService.getRelatedPoints(lines)[0];
        const internalSide = this.attributes[this.ANGLE_SIDE.PROPERTY] === this.ANGLE_SIDE.VALUES.INTERNAL;

        let fromLine: any;
        let toLine: any;

        if (internalSide) {
            fromLine = lines[0];
            toLine = lines[1];
        } else {
            fromLine = lines[1];
            toLine = lines[0];
        }

        const angleProperties = {attributes: structuredClone(this.attributes)};
        angleProperties.attributes.pointid = point.attributes.id;
        angleProperties.attributes.anglefromlineid = fromLine.attributes.id;
        angleProperties.attributes.angletolineid = toLine.attributes.id;
        angleProperties.attributes.internalangle =
            this.attributes.internalangle !== undefined && this.attributes.internalangle !== '' ? this.attributes.internalangle : undefined;
        angleProperties.attributes.measurementsource = this.attributes.measurementsource.id;

        const angle = this.angleUtilService.completeAnglesCreation(
            [angleProperties],
            [point],
            [fromLine, toLine],
            internalSide,
            'Generated in portal'
        )[0];

        this.setVisibilityAttributes([angle]);

        this.removeUnnecessaryAttributes(angle);

        const existingAngles = this.getExistingAngles(fromLine.attributes.id, toLine.attributes.id);

        if (existingAngles.length > 0) {
            this.updateExistingAngleAttributes(existingAngles[0], angle);

            return this.updateAngles([existingAngles[0]]);
        } else {
            return this.angleCreationService.insertAngles([angle]);
        }
    }

    private getExistingAngles(fromLineId: number, toLineId: number) {
        console.debug(`${this.title} - getExistingAngle`);

        const existingAngles = this.spatialUtilService
            .getAngles()
            .filter((a: any) => !a.attributes.enlargementid)
            .filter((a: any) => parseInt(a.attributes.anglefromlineid) === fromLineId && parseInt(a.attributes.angletolineid) === toLineId);

        return existingAngles.reduce((acc: any, v: any) => {
            acc.push(new this.esriModuleService.Graphic(v.toJson()));
            return acc;
        }, []);
    }

    private updateExistingAngleAttributes(existingAngle: any, newAngle: any) {
        console.debug(`${this.title} - updateExistingAngleAttributes`);

        existingAngle.attributes.type = newAngle.attributes.type;
        existingAngle.attributes.internalangle = newAngle.attributes.internalangle;
        existingAngle.attributes.anglederivation = newAngle.attributes.anglederivation;
        existingAngle.attributes.angleaccuracy = newAngle.attributes.angleaccuracy;
        existingAngle.attributes.measurementsource = newAngle.attributes.measurementsource;
        existingAngle.attributes.hiddenonenlargements = newAngle.attributes.hiddenonenlargements;
        existingAngle.attributes.visible = newAngle.attributes.visible;
    }

    private removeUnnecessaryAttributes(angle: any) {
        console.debug(`${this.title} - removeUnnecessaryAttributes`);

        delete angle.attributes[this.CREATION_METHOD.PROPERTY];
        delete angle.attributes[this.ANGLE_SIDE.PROPERTY];
        delete angle.attributes[this.HIDE_180_DEGREES.PROPERTY];
        delete angle.attributes[this.HIDE_ON_MAIN_PLAN.PROPERTY];
        delete angle.attributes[this.HIDE_ON_ENLARGEMENTS.PROPERTY];
        delete angle.attributes[this.CREATE_INTEREST_ANGLES.PROPERTY];
    }

    private updateAngles(angles: any[]) {
        console.debug(`${this.title} - updateAngles`);

        return this.featuresApplyUpdateService
            .updateFeaturesByType(this.spatialUtilService.getAngleMapLayer(), angles)
            .pipe(tap(() => console.debug('Angles have been successfully updated!')));
    }
}
